import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const GOLD = '#C9A84C';
const BG = '#050810';
const SURFACE = '#0B1120';
const BORDER = 'rgba(255,255,255,0.07)';
const OFF_WHITE = '#A0AABB';
const RED = '#FF4444';
const GREEN = '#22C55E';

const s = {
  page: {
    background: BG,
    color: '#fff',
    fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    minHeight: '100vh',
  },
  wrap: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '72px 24px 80px',
  },
  badge: {
    display: 'inline-block',
    background: 'rgba(201,168,76,0.12)',
    border: '1px solid rgba(201,168,76,0.3)',
    color: GOLD,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '6px 14px',
    borderRadius: 20,
    marginBottom: 24,
  },
  h1: {
    fontSize: 'clamp(28px,5vw,42px)',
    fontWeight: 800,
    margin: '0 0 16px',
    lineHeight: 1.15,
  },
  sub: {
    color: OFF_WHITE,
    fontSize: 17,
    lineHeight: 1.7,
    margin: '0 0 56px',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: OFF_WHITE,
    marginBottom: 20,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 48,
  },
  thMane: {
    background: 'rgba(201,168,76,0.1)',
    color: GOLD,
    fontWeight: 700,
    fontSize: 13,
    padding: '14px 20px',
    textAlign: 'left',
    border: '1px solid rgba(201,168,76,0.2)',
    borderBottom: `2px solid ${GOLD}`,
  },
  thComp: {
    background: SURFACE,
    color: OFF_WHITE,
    fontWeight: 700,
    fontSize: 13,
    padding: '14px 20px',
    textAlign: 'left',
    border: `1px solid ${BORDER}`,
  },
  thLabel: {
    background: 'transparent',
    color: OFF_WHITE,
    fontWeight: 600,
    fontSize: 12,
    padding: '14px 0',
    textAlign: 'left',
    letterSpacing: '0.04em',
    border: 'none',
    borderBottom: `1px solid ${BORDER}`,
  },
  tdLabel: {
    color: OFF_WHITE,
    fontSize: 14,
    padding: '16px 0',
    borderBottom: `1px solid ${BORDER}`,
    fontWeight: 500,
    verticalAlign: 'top',
  },
  tdMane: {
    color: '#fff',
    fontSize: 14,
    padding: '16px 20px',
    borderBottom: '1px solid rgba(201,168,76,0.15)',
    background: 'rgba(201,168,76,0.04)',
    border: '1px solid rgba(201,168,76,0.1)',
    verticalAlign: 'top',
  },
  tdComp: {
    color: OFF_WHITE,
    fontSize: 14,
    padding: '16px 20px',
    borderBottom: `1px solid ${BORDER}`,
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    verticalAlign: 'top',
  },
  callout: {
    background: 'rgba(201,168,76,0.07)',
    border: '1px solid rgba(201,168,76,0.25)',
    borderRadius: 12,
    padding: '28px 32px',
    marginBottom: 48,
  },
  calloutLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: GOLD,
    marginBottom: 12,
  },
  calloutText: {
    color: '#fff',
    fontSize: 16,
    lineHeight: 1.8,
    margin: 0,
  },
  faqItem: {
    borderBottom: `1px solid ${BORDER}`,
    paddingBottom: 28,
    marginBottom: 28,
  },
  faqQ: {
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    marginBottom: 10,
  },
  faqA: {
    color: OFF_WHITE,
    fontSize: 15,
    lineHeight: 1.7,
    margin: 0,
  },
  cta: {
    background: 'rgba(201,168,76,0.07)',
    border: '1px solid rgba(201,168,76,0.25)',
    borderRadius: 16,
    padding: '48px 40px',
    textAlign: 'center',
    marginTop: 56,
  },
  ctaTitle: {
    fontSize: 24,
    fontWeight: 800,
    marginBottom: 12,
  },
  ctaSub: {
    color: OFF_WHITE,
    fontSize: 15,
    lineHeight: 1.6,
    marginBottom: 32,
  },
  btn: {
    display: 'inline-block',
    background: GOLD,
    color: '#000',
    fontWeight: 800,
    fontSize: 14,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '16px 40px',
    borderRadius: 8,
    textDecoration: 'none',
  },
};

const rows = [
  {
    label: 'Monthly Price',
    mane: '$29/mo — locked forever',
    comp: <span>$24/mo (Standard) or $48/mo (Gold)<br /><span style={{ fontSize: 12, color: OFF_WHITE }}>Most features locked behind Gold tier</span></span>,
  },
  {
    label: 'Payment Processing',
    mane: <span style={{ color: GREEN }}>Standard Stripe (2.9% + 30c)</span>,
    comp: <span style={{ color: RED }}>2.6% processing fee on every transaction<br /><span style={{ fontSize: 12 }}>Proprietary processor — no choice</span></span>,
  },
  {
    label: 'Commission on Bookings',
    mane: <span style={{ color: GREEN }}>0% — always</span>,
    comp: <span>No commission, but processing fees are mandatory</span>,
  },
  {
    label: 'Client Data Ownership',
    mane: <span style={{ color: GREEN }}>Yours. Export anytime.</span>,
    comp: <span style={{ color: RED }}>Limited export options. Switching is painful.</span>,
  },
  {
    label: 'Website / Booking Page',
    mane: <span style={{ color: GREEN }}>Included — custom booking link</span>,
    comp: 'Included — nice templates',
  },
  {
    label: 'AI Re-Engagement',
    mane: <span style={{ color: GREEN }}>Included — auto win-back messages</span>,
    comp: <span style={{ color: RED }}>Not available</span>,
  },
  {
    label: 'SMS Reminders',
    mane: <span style={{ color: GREEN }}>Included</span>,
    comp: 'Included',
  },
  {
    label: 'Reports & Analytics',
    mane: <span style={{ color: GREEN }}>Included</span>,
    comp: 'Gold tier only ($48/mo)',
  },
  {
    label: 'Contract',
    mane: 'Month-to-month',
    comp: 'Month-to-month',
  },
];

const faqs = [
  {
    q: 'How does GlossGenius pricing compare to Mane?',
    a: "GlossGenius Standard is $24/mo, which sounds competitive. But their Gold plan at $48/mo is where the real features are — reports, analytics, team management, priority support. Plus, every single transaction goes through their payment processor at 2.6%. On $8,000/mo in revenue, that's $208/mo in processing fees you can't avoid. Mane is $29/mo with all features included.",
  },
  {
    q: 'Is GlossGenius good for independent pros?',
    a: "GlossGenius markets heavily to independent beauty professionals, and their design is polished. But the two-tier pricing means you're constantly deciding whether a feature is worth upgrading for. With Mane, there's one plan. You get everything. No upgrade decisions, no feature jealousy.",
  },
  {
    q: "Can I use my own payment processor with GlossGenius?",
    a: "No. GlossGenius requires their built-in payment processing on every transaction. You can't bring your own Stripe account. This means they earn revenue on every dollar you collect, forever. With Mane, you connect your own Stripe account — the processing relationship is between you and Stripe, not us.",
  },
  {
    q: "What does GlossGenius have that Mane doesn't?",
    a: "GlossGenius has a branded card reader for in-person payments, a polished website builder with more templates, and a client-facing app. If you value premium design polish in your booking site above all else, GlossGenius looks good. If you care more about keeping your money, owning your data, and AI tools that bring clients back automatically, Mane delivers more value.",
  },
  {
    q: "What happens to my price if Mane raises rates?",
    a: "Founding members are locked at $29/mo for life. That's in your membership agreement. When we raise prices for new members, your rate doesn't move. Ever.",
  },
];

export default function VsGlossGenius() {
  return (
    <div style={s.page}>
      <Helmet>
        <title>Mane vs. GlossGenius — Which One Keeps More of Your Money?</title>
        <meta name="description" content="GlossGenius looks pretty but charges 2.6% on every transaction and locks real features behind the $48/mo Gold plan. Here's the honest comparison with Mane." />
        <meta property="og:title" content="Mane vs. GlossGenius — Which One Keeps More of Your Money?" />
        <meta property="og:description" content="GlossGenius takes 2.6% of every dollar you earn. Real features cost $48/mo. Here's what the comparison actually looks like." />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://joinmane.com/og-image-v8.png" />
      </Helmet>
      <div style={s.wrap}>
        <div style={s.badge}>Mane vs. GlossGenius</div>
        <h1 style={s.h1}>Pretty design. Quiet fees.</h1>
        <p style={s.sub}>
          GlossGenius has clean branding and nice templates. It also takes 2.6% of every
          transaction and locks analytics behind a $48/mo paywall. Let's look at what you're
          actually paying for.
        </p>

        <div style={s.sectionTitle}>Side-by-side breakdown</div>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.thLabel}></th>
              <th style={s.thMane}>Mane (Founding)</th>
              <th style={s.thComp}>GlossGenius</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={s.tdLabel}>{r.label}</td>
                <td style={s.tdMane}>{r.mane}</td>
                <td style={s.tdComp}>{r.comp}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={s.callout}>
          <div style={s.calloutLabel}>Real math</div>
          <p style={s.calloutText}>
            Nail tech collecting $6,000/mo through GlossGenius.<br />
            GlossGenius Gold: $48/mo + 2.6% processing on $6,000 = <strong style={{ color: RED }}>$48 + $156 = $204/mo.</strong><br />
            That's <strong style={{ color: RED }}>$2,448/year</strong> going to your booking app.<br /><br />
            Mane founding rate: <strong style={{ color: GREEN }}>$29/mo + standard Stripe rates.</strong><br />
            You keep more of what you earned.
          </p>
        </div>

        <div style={s.sectionTitle}>Common questions</div>
        {faqs.map((f, i) => (
          <div key={i} style={s.faqItem}>
            <div style={s.faqQ}>{f.q}</div>
            <p style={s.faqA}>{f.a}</p>
          </div>
        ))}

        <div style={s.cta}>
          <div style={s.ctaTitle}>Keep more of what you earn.</div>
          <p style={s.ctaSub}>
            50 founding spots. Pay $199 once, then $29/mo locked for life.<br />
            When the spots fill, the price goes to $59/mo — no exceptions.
          </p>
          <Link to="/founding" style={s.btn}>Claim Your Founding Spot</Link>
          <p style={{ color: OFF_WHITE, fontSize: 13, marginTop: 20 }}>
            Questions? <a href="mailto:christiangflairbiz@gmail.com" style={{ color: GOLD }}>Email Christian directly.</a> He answers.
          </p>
        </div>
      </div>
    </div>
  );
}
